import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BatchAnalysisPanel from '@/components/BatchAnalysisPanel';
import { ConnectionError } from '@/components/ErrorScenes';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Search, FileText, Clock, Loader2, CheckCircle2, AlertCircle, Trash2, MoreVertical, Filter, Layers, Calendar, Trophy, XCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProjectsList() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [search, setSearch] = useState('');
  const [bidFilter, setBidFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [showBatch, setShowBatch] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newBidStart, setNewBidStart] = useState('');
  const [newBidEnd, setNewBidEnd] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => { fetchProjects(); }, [bidFilter]);

  const fetchProjects = async () => {
    try {
      const params = bidFilter !== 'all' ? `?bid_status=${bidFilter}` : '';
      const res = await fetch(`${API}/projects${params}`, { credentials: 'include' });
      if (res.ok) {
        setProjects(await res.json());
        setLoadError(false);
      } else {
        setLoadError(true);
      }
    } catch (e) { console.error(e); setLoadError(true); }
    finally { setLoading(false); }
  };

  const createProject = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const res = await fetch(`${API}/projects`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({
          name: newName,
          description: newDesc,
          bid_start_date: newBidStart || null,
          bid_end_date: newBidEnd || null
        })
      });
      if (res.ok) {
        const proj = await res.json();
        setShowCreate(false); setNewName(''); setNewDesc(''); setNewBidStart(''); setNewBidEnd('');
        navigate(`/projects/${proj.project_id}`);
      }
    } catch (e) { console.error(e); }
    finally { setCreating(false); }
  };

  const deleteProject = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Delete this project and all data?')) return;
    try {
      await fetch(`${API}/projects/${id}`, { method: 'DELETE', credentials: 'include' });
      setProjects(prev => prev.filter(p => p.project_id !== id));
    } catch (e) { console.error(e); }
  };

  const filtered = projects.filter(p =>
    !search || p.name?.toLowerCase().includes(search.toLowerCase()) || p.description?.toLowerCase().includes(search.toLowerCase())
  );

  const statusConfig = {
    pending: { label: 'Pending', color: 'bg-[#F0F4F7] text-[#446E87]', icon: Clock },
    active: { label: 'Active', color: 'bg-blue-100 text-blue-700', icon: FileText },
    analyzing: { label: 'Analyzing', color: 'bg-[#72A4BF]/10 text-[#0D1830]', icon: Loader2 },
    completed: { label: 'Completed', color: 'bg-[#E8F5E9] text-[#2E7D32]', icon: CheckCircle2 },
    quoted: { label: 'Quoted', color: 'bg-purple-100 text-purple-700', icon: FileText },
    won: { label: 'Won', color: 'bg-green-100 text-green-700', icon: Trophy },
    lost: { label: 'Lost', color: 'bg-red-100 text-red-700', icon: XCircle },
    expired: { label: 'Expired', color: 'bg-orange-100 text-orange-700', icon: Clock },
    error: { label: 'Error', color: 'bg-[#FFEBEE] text-[#C62828]', icon: AlertCircle },
  };

  const formatDate = (d) => {
    if (!d) return null;
    try { return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); }
    catch { return d; }
  };

  const getBidStatus = (p) => {
    const today = new Date().toISOString().split('T')[0];
    if (!p.bid_end_date) return null;
    if (p.status === 'expired') return { label: 'Expired', color: 'text-orange-600' };
    if (p.bid_end_date < today) return { label: 'Overdue', color: 'text-red-500' };
    const daysLeft = Math.ceil((new Date(p.bid_end_date) - new Date(today)) / (1000 * 60 * 60 * 24));
    if (daysLeft <= 3) return { label: `${daysLeft}d left`, color: 'text-orange-500' };
    if (daysLeft <= 7) return { label: `${daysLeft}d left`, color: 'text-yellow-600' };
    return { label: `${daysLeft}d left`, color: 'text-[#446E87]' };
  };

  return (
    <div className="min-h-full bg-[#F7FAFB]" data-testid="projects-page">
      <main className="max-w-[1440px] mx-auto px-6 lg:px-10 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <h1 className="text-2xl font-bold text-[#0D1830]">Projects</h1>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#72A4BF]" />
              <Input placeholder="Search projects..." value={search} onChange={(e) => setSearch(e.target.value)}
                className="pl-9 w-64 rounded-full h-9 text-sm" data-testid="search-projects" />
            </div>
            <Select value={bidFilter} onValueChange={setBidFilter}>
              <SelectTrigger className="w-36 rounded-full h-9 text-sm" data-testid="bid-filter">
                <Filter className="w-3.5 h-3.5 mr-1.5 text-[#72A4BF]" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Projects</SelectItem>
                <SelectItem value="active_bids">Active Bids</SelectItem>
                <SelectItem value="upcoming">Upcoming</SelectItem>
                <SelectItem value="expired_bids">Expired</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              onClick={() => setShowBatch(true)}
              className="rounded-full px-4 h-9 text-sm font-medium border-[#1D4053]/30 text-[#0D1830] hover:bg-[#72A4BF]/10 hover:text-[#0D1830]"
              data-testid="batch-analysis-btn"
            >
              <Layers className="w-4 h-4 mr-1.5" />Batch Analysis
            </Button>
            <Button onClick={() => setShowCreate(true)} className="bg-[#0D1830] text-white rounded-full px-5 h-9 text-sm font-semibold hover:opacity-90" data-testid="create-project-btn">
              <Plus className="w-4 h-4 mr-1.5" />New Project
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-[#1D4053] animate-spin" /></div>
        ) : loadError ? (
          <ConnectionError onRetry={fetchProjects} message="Unable to load projects. Please check your connection and try again." />
        ) : filtered.length === 0 ? (
          <Card className="border-dashed border-2 border-[#E2E8ED] shadow-none bg-transparent" data-testid="empty-projects">
            <CardContent className="py-16 text-center">
              <div className="w-14 h-14 rounded-2xl bg-[#72A4BF]/10 flex items-center justify-center mx-auto mb-4">
                <FileText className="w-7 h-7 text-[#0D1830]" />
              </div>
              <h3 className="text-base font-semibold text-[#0D1830] mb-1">{search ? 'No matching projects' : 'No projects yet'}</h3>
              <p className="text-sm text-[#72A4BF] mb-5">{search ? 'Try a different search term' : 'Create your first project to start analyzing tender documents'}</p>
              {!search && <Button onClick={() => setShowCreate(true)} className="bg-[#0D1830] text-white rounded-full px-6 hover:opacity-90"><Plus className="w-4 h-4 mr-1.5" />Create Project</Button>}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((p) => {
              const sc = statusConfig[p.status] || statusConfig.pending;
              const SI = sc.icon;
              const bidStatus = getBidStatus(p);
              return (
                <Card key={p.project_id} onClick={() => navigate(`/projects/${p.project_id}`)}
                  className={`cursor-pointer border-[#E2E8ED] shadow-none hover:border-[#1D4053]/25 hover:shadow-md hover:shadow-[#1D4053]/5 transition-all duration-200 group ${p.status === 'expired' ? 'border-orange-200/60' : ''}`}
                  data-testid={`project-card-${p.project_id}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-sm font-semibold text-[#0D1830] group-hover:text-[#0D1830] transition-colors line-clamp-1">{p.name}</CardTitle>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button onClick={(e) => e.stopPropagation()} className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-[#72A4BF]/8">
                            <MoreVertical className="w-4 h-4 text-[#72A4BF]" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                          <DropdownMenuItem onClick={(e) => deleteProject(e, p.project_id)} className="text-red-600">
                            <Trash2 className="w-4 h-4 mr-2" />Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    {p.description && <p className="text-xs text-[#72A4BF] line-clamp-2 mt-1">{p.description}</p>}
                  </CardHeader>
                  <CardContent className="pt-0">
                    {/* Bid dates row */}
                    {(p.bid_start_date || p.bid_end_date) && (
                      <div className="flex items-center gap-2 mb-2 text-[11px]" data-testid={`bid-dates-${p.project_id}`}>
                        <Calendar className="w-3 h-3 text-[#72A4BF] shrink-0" />
                        <span className="text-[#446E87]">
                          {p.bid_start_date && formatDate(p.bid_start_date)}
                          {p.bid_start_date && p.bid_end_date && ' — '}
                          {p.bid_end_date && formatDate(p.bid_end_date)}
                        </span>
                        {bidStatus && (
                          <span className={`font-semibold ${bidStatus.color}`}>{bidStatus.label}</span>
                        )}
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-[#72A4BF]/60">{p.file_count || 0} files</span>
                      <Badge className={`${sc.color} border-0 text-xs rounded-full px-2 py-0.5 font-medium`}>
                        <SI className={`w-3 h-3 mr-1 ${p.status === 'analyzing' ? 'animate-spin' : ''}`} />{sc.label}
                      </Badge>
                    </div>
                    <p className="text-xs text-[#72A4BF]/60 mt-2">{new Date(p.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>

      {/* Create Project Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-md" data-testid="create-dialog">
          <DialogHeader>
            <DialogTitle>New Project</DialogTitle>
            <DialogDescription>Create a new construction takeoff project</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium text-[#0D1830] mb-1.5 block">Project Name</label>
              <Input data-testid="project-name-input" placeholder="e.g., Downtown Office Tower Bid" value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && createProject()} />
            </div>
            <div>
              <label className="text-sm font-medium text-[#0D1830] mb-1.5 block">Description</label>
              <Textarea data-testid="project-desc-input" placeholder="Brief description..." value={newDesc} onChange={(e) => setNewDesc(e.target.value)} rows={2} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-[#0D1830] mb-1.5 block">Start Date</label>
                <Input data-testid="bid-start-input" type="date" value={newBidStart} onChange={(e) => setNewBidStart(e.target.value)} />
              </div>
              <div>
                <label className="text-sm font-medium text-[#0D1830] mb-1.5 block">End Date</label>
                <Input data-testid="bid-end-input" type="date" value={newBidEnd} onChange={(e) => setNewBidEnd(e.target.value)} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)} className="rounded-full">Cancel</Button>
            <Button data-testid="confirm-create-btn" onClick={createProject} disabled={!newName.trim() || creating} className="bg-[#0D1830] text-white rounded-full hover:opacity-90">
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Plus className="w-4 h-4 mr-1.5" />}Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Batch Analysis Panel */}
      {showBatch && (
        <BatchAnalysisPanel
          projects={projects}
          onClose={() => setShowBatch(false)}
          onQueueUpdated={fetchProjects}
        />
      )}
    </div>
  );
}
